// src/components/VisitorContactWidget/components/CallbackRequestForm.jsx

import React, { useState } from "react";

import {
  FiCalendar,
  FiLoader,
  FiMail,
  FiPhone,
  FiSend,
  FiUser,
  FiUsers,
} from "react-icons/fi";

import {
  CALLBACK_TIME_OPTIONS,
  PRODUCT_OPTIONS,
} from "../data/contactWidgetData";

function Field({
  label,
  icon: Icon,
  required,
  children,
}) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
        {Icon && <Icon />}
        {label}
        {required && (
          <span className="text-red-500">*</span>
        )}
      </span>

      {children}
    </label>
  );
}

export default function CallbackRequestForm({
  submitting,
  errorMessage,
  storedContact,
  suggestedProduct,
  onSubmit,
}) {
  const [form, setForm] = useState({
    name: storedContact?.name || "",
    organization_name:
      storedContact?.organization_name || "",
    email: storedContact?.email || "",
    phone: storedContact?.phone || "",
    preferred_date: "",
    preferred_time: "",
    product_interest: suggestedProduct || "",
    message: "",
  });

  const updateField = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      !form.name.trim() ||
      !form.phone.trim()
    ) {
      return;
    }

    await onSubmit({
      ...form,
      name: form.name.trim(),
      organization_name:
        form.organization_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      message: form.message.trim(),
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 p-5"
    >
      <div className="rounded-2xl bg-amber-50 p-4 text-sm leading-6 text-amber-800">
        Indiquez vos coordonnées et le meilleur moment
        pour vous joindre.
      </div>

      <Field
        label="Votre prénom et nom"
        icon={FiUser}
        required
      >
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={updateField}
          required
          autoComplete="name"
          className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm outline-none focus:border-[#2EA7E0]"
        />
      </Field>

      <Field
        label="Club ou association"
        icon={FiUsers}
      >
        <input
          type="text"
          name="organization_name"
          value={form.organization_name}
          onChange={updateField}
          className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm outline-none focus:border-[#2EA7E0]"
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Téléphone"
          icon={FiPhone}
          required
        >
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={updateField}
            required
            autoComplete="tel"
            placeholder="06 12 34 56 78"
            className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm outline-none focus:border-[#2EA7E0]"
          />
        </Field>

        <Field
          label="Email"
          icon={FiMail}
        >
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={updateField}
            autoComplete="email"
            className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm outline-none focus:border-[#2EA7E0]"
          />
        </Field>
      </div>

      <Field label="Application concernée">
        <select
          name="product_interest"
          value={form.product_interest}
          onChange={updateField}
          className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm outline-none focus:border-[#2EA7E0]"
        >
          {PRODUCT_OPTIONS.map((option) => (
            <option
              key={option.value}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Jour souhaité"
          icon={FiCalendar}
        >
          <input
            type="date"
            name="preferred_date"
            value={form.preferred_date}
            onChange={updateField}
            min={new Date()
              .toISOString()
              .slice(0, 10)}
            className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm outline-none focus:border-[#2EA7E0]"
          />
        </Field>

        <Field label="Créneau">
          <select
            name="preferred_time"
            value={form.preferred_time}
            onChange={updateField}
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm outline-none focus:border-[#2EA7E0]"
          >
            {CALLBACK_TIME_OPTIONS.map(
              (option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ),
            )}
          </select>
        </Field>
      </div>

      <Field label="Précisions">
        <textarea
          name="message"
          value={form.message}
          onChange={updateField}
          rows={3}
          placeholder="Indiquez votre besoin..."
          className="w-full resize-none rounded-xl border border-slate-200 px-3.5 py-3 text-sm outline-none focus:border-[#2EA7E0]"
        />
      </Field>

      {errorMessage && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={
          submitting ||
          !form.name.trim() ||
          !form.phone.trim()
        }
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#123B57] px-4 py-3.5 text-sm font-semibold text-white transition hover:bg-[#1C567F] disabled:opacity-50"
      >
        {submitting ? (
          <>
            <FiLoader className="animate-spin" />
            Enregistrement...
          </>
        ) : (
          <>
            <FiSend />
            Demander à être rappelé
          </>
        )}
      </button>
    </form>
  );
}